/**
 * ============================================================================
 * MM Cart Drawer - Extended Cart Functionality
 * For: New Prestige Theme (Web Components Architecture)
 * Features: Geolocation Shipping, Cheering Bar, Dual Free Gift, dCart, Price Summary
 * ============================================================================
 *
 * GIFT SYSTEM:
 * - Gift 1: Auto-add, single product, threshold-based, no customer choice
 * - Gift 2: Customer choice, two tiers with expanding product selection
 *
 * SAFETY:
 * - Processing guards prevent parallel execution
 * - Fresh cart check before every add (race condition prevention)
 * - Availability check before every add
 * - Tag-based qty enforcement (freegift / free-gift tags)
 */

"use strict";

(() => {
  const DEBUG = false;

  /* ================================================================
   * STATE
   * ================================================================ */

  const state = {
    country: null,
    minFreeShipping: 0,
    shippingRate: 0,
    isGiftProcessing: false,
    isQtyEnforcementRunning: false,
    isOurUpdate: false,
    lastCartHash: null,
    lastCartToken: null,
  };

  /* ================================================================
   * CONFIG (von Liquid injiziert als window.mmCartConfig)
   * ================================================================ */

  const cfg = () => window.mmCartConfig || {};

  /* ================================================================
   * HELPERS
   * ================================================================ */

  const formatMoney = (cents) => {
    if (typeof Shopify?.scFormatMoney === "function") return Shopify.scFormatMoney(cents);
    if (typeof Shopify?.formatMoney === "function") return Shopify.formatMoney(cents);
    return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
  };

  const fetchCart = async () => {
    const resp = await fetch("/cart.js", { credentials: "same-origin" });
    if (!resp.ok) throw new Error("cart.js fetch failed: " + resp.status);
    return resp.json();
  };

  // 30s TTL cache for /variants/{id}.js — availability rarely flips in seconds and
  // re-checking on every cart-drawer update was the main source of /variants/ polling.
  const variantAvailabilityCache = new Map();
  const VARIANT_CACHE_TTL_MS = 30_000;

  const isVariantAvailable = async (variantId) => {
    const cached = variantAvailabilityCache.get(variantId);
    if (cached && Date.now() - cached.t < VARIANT_CACHE_TTL_MS) {
      return cached.v;
    }
    try {
      const resp = await fetch(`/variants/${variantId}.js`, { credentials: "same-origin" });
      if (!resp.ok) {
        if (DEBUG) console.warn(`[MM Cart] Variant ${variantId} not available (HTTP ${resp.status})`);
        variantAvailabilityCache.set(variantId, { v: false, t: Date.now() });
        return false;
      }
      const data = await resp.json();
      const available = !!data.available;
      if (!available && DEBUG) console.warn(`[MM Cart] Variant ${variantId} sold out or unpublished`);
      variantAvailabilityCache.set(variantId, { v: available, t: Date.now() });
      return available;
    } catch (err) {
      console.warn(`[MM Cart] Availability check failed for ${variantId}:`, err);
      return false;
    }
  };

  /* ================================================================
   * CART DERIVATIONS (single source of truth = /cart.js)
   *
   * All gift-logic and cheering-bar math reads from the live cart
   * object instead of stale DOM attributes on <cart-drawer>. Liquid
   * is responsible for rendering the visible Subtotal / Total /
   * Checkout-Button price, JS only touches the country-dependent
   * shipping line.
   * ================================================================ */

  const getCartDrawer = () => document.querySelector("#cart-drawer");

  /**
   * Cart subtotal in EUR, after cart-level discounts.
   * Mirrors the Liquid `cart_subtotal` calculation in cart-drawer.liquid:
   * all items (incl. digital) summed by `final_line_price`, minus the
   * sum of `cart_level_discount_applications.total_allocated_amount`.
   * Digital-only baskets are filtered out at the gift-trigger level
   * via hasShippableItems().
   */
  const computeCartSubtotal = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return 0;
    const lineSum = cart.items.reduce(
      (sum, item) => sum + (item.final_line_price || 0),
      0
    );
    const cartDiscount = (cart.cart_level_discount_applications || []).reduce(
      (sum, d) => sum + (d.total_allocated_amount || 0),
      0
    );
    return (lineSum - cartDiscount) / 100;
  };

  /**
   * True iff the cart contains at least one shippable item that is
   * NOT a configured gift. The gift itself is usually a shippable
   * product — without this exclusion, a basket containing only a
   * leftover gift after the customer removed all real items would
   * still register as "shippable" and the cleanup path would never
   * fire.
   */
  const hasShippableItems = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some((i) => {
      if (i.requires_shipping === false) return false;
      if (isStageGiftItem(i)) return false;   // any variant of any stage product counts as gift
      return true;
    });
  };

  /* ================================================================
   * CHECKOUT BUTTON LOCK/UNLOCK
   * ================================================================ */

  window.lockCheckoutButton = () => {
    const btn = document.querySelector("#mm-checkout-button, .cart-drawer [name='checkout']");
    if (btn) btn.disabled = true;
  };

  window.unlockCheckoutButton = () => {
    const btn = document.querySelector("#mm-checkout-button, .cart-drawer [name='checkout']");
    if (btn) btn.disabled = false;
  };

  /* ================================================================
   * GEOLOCATION
   * ================================================================ */

  const SHIPPING_COUNTRY_CACHE_KEY = "mmShippingCountry";

  /**
   * Publishes the resolved shipping context so other components can use the
   * SAME threshold instead of re-deriving one. The Cheering Bar
   * (cart-progress-bar.liquid) listens for this — before it did, the bar
   * computed its free-shipping marker from the DE default while the shipping
   * line in the price summary already used the detected country's value, so
   * an AT customer saw two different thresholds in one drawer.
   */
  const publishShippingContext = () => {
    window.mmShippingContext = {
      country: state.country,
      minFreeShipping: state.minFreeShipping,
      shippingRate: state.shippingRate,
    };
    document.dispatchEvent(
      new CustomEvent("mm:shipping-context", { detail: window.mmShippingContext })
    );
  };

  /** Applies a country code to state (rates come from the Liquid config). */
  const applyCountry = (country) => {
    const rates = cfg().shippingRates || {};
    state.country = country;
    if (rates[country]) {
      state.minFreeShipping = parseFloat(rates[country].minFreeShipping);
      state.shippingRate = parseFloat(rates[country].rate);
    } else {
      state.minFreeShipping = 0;
      state.shippingRate = 0;
    }
    publishShippingContext();
  };

  const detectCountry = async () => {
    // Result of an earlier lookup in this session: apply it right away so the
    // shipping marker starts in the right place instead of visibly moving once
    // the network call returns. Still refreshed from the server below.
    let cached = null;
    try {
      cached = sessionStorage.getItem(SHIPPING_COUNTRY_CACHE_KEY);
    } catch (e) {
      /* sessionStorage can throw in private mode — cache is optional */
    }
    if (cached) {
      applyCountry(cached);
      update();
    }

    try {
      const response = await fetch("/browsing_context_suggestions.json");
      const data = await response.json();
      const country = data.detected_values.country.handle;

      try {
        sessionStorage.setItem(SHIPPING_COUNTRY_CACHE_KEY, country);
      } catch (e) {
        /* ignore */
      }

      if (country !== cached) {
        applyCountry(country);
        update();
      }
    } catch (err) {
      console.warn("[MM Cart] Geolocation failed, using DE defaults:", err);
      if (!cached) {
        const rates = cfg().shippingRates || {};
        state.minFreeShipping = parseFloat(rates.DE?.minFreeShipping || 49);
        state.shippingRate = parseFloat(rates.DE?.rate || 4.9);
        state.country = "DE";
        publishShippingContext();
        update();
      }
    }
  };

  /* ================================================================
   * GIFT STAGE HELPERS
   * ================================================================ */

  /** Returns the configured stages array (always 4 entries, possibly all disabled). */
  const getStages = () => cfg().stages || [];

  /** Returns only enabled stages. */
  const getEnabledStages = () => getStages().filter((s) => s && s.enabled);

  /** All variant IDs that appear in any enabled stage's product list. */
  const getAllStageVariantIds = () => {
    const ids = [];
    getEnabledStages().forEach((stage) => {
      (stage.products || []).forEach((p) => ids.push(parseInt(p.variantId)));
    });
    return ids;
  };

  /** All product IDs that appear in any enabled stage's product list.
   *  Used in addition to variant IDs so a gift that landed in the cart
   *  with a different variant of the same product (e.g. due to a stale
   *  selected_or_first_available_variant captured at section render
   *  time, or a different add-path that picked another variant) is
   *  still recognised as a stage gift. */
  const getAllStageProductIds = () => {
    const ids = [];
    getEnabledStages().forEach((stage) => {
      (stage.products || []).forEach((p) => {
        if (p.productId) ids.push(parseInt(p.productId));
      });
    });
    return ids;
  };

  /** Is the given cart line a gift line? Matches via three rails:
   *    1. variant_id is in some enabled stage
   *    2. product_id is in some enabled stage (different variant of
   *       the same stage product)
   *    3. variant_id is in mmCartConfig.freegiftCartVariantIds —
   *       i.e. the cart-side Liquid pass saw a `freegift`/`free-gift`
   *       product tag on this line. Catches orphans from old stage
   *       configurations that still carry the gift tag but no longer
   *       appear in any enabled stage's product list. */
  const isStageGiftItem = (cartItem) => {
    if (!cartItem) return false;
    const variantIds = getAllStageVariantIds();
    if (variantIds.includes(cartItem.variant_id)) return true;
    const productIds = getAllStageProductIds();
    if (productIds.includes(cartItem.product_id)) return true;
    const taggedGifts = (window.mmCartConfig && window.mmCartConfig.freegiftCartVariantIds) || [];
    if (taggedGifts.includes(cartItem.variant_id)) return true;
    return false;
  };

  /** Find the stage object (and its 1-based index) that contains a given variant. */
  const findStageByVariantId = (variantId) => {
    const stages = getStages();
    for (let i = 0; i < stages.length; i++) {
      const stage = stages[i];
      if (!stage || !stage.enabled) continue;
      if ((stage.products || []).some((p) => parseInt(p.variantId) === variantId)) {
        return { stage, index: stage.index || i + 1 };
      }
    }
    return null;
  };

  /**
   * Subtotal used as the gift threshold. Same logic as the displayed
   * Drawer Subtotal (computeCartSubtotal) minus all configured gift
   * variants so the act of adding a free gift can't push the basket
   * past the next stage's threshold by itself.
   *
   * Digital items ARE included — see hasShippableItems() above for the
   * digital-only short-circuit that prevents gifts on download-only
   * orders.
   */
  const calcSubtotalExcludingGifts = (cart) => {
    const stageVariantIds = new Set(getAllStageVariantIds());
    // Sum line-item totals AFTER per-line discounts but BEFORE cart-level
    // discounts. Gift thresholds are evaluated on the customer's
    // pre-cart-discount basket value so applying a coupon does not yank a
    // gift back out (which was the bug: a 10% code dropped the
    // discount-adjusted subtotal below the next stage threshold and
    // enforceGiftQtyCaps removed the gift). Keep cart_level_discount_applications
    // OUT of this number. The displayed "Subtotal" (computeCartSubtotal)
    // does subtract them — that one shows what the customer pays.
    const lineSum = (cart.items || []).reduce((sum, item) => {
      if (stageVariantIds.has(item.variant_id)) return sum;
      return sum + (item.final_line_price || 0);
    }, 0);
    return lineSum / 100;
  };

  /** Did the customer decline this stage in the current tab session? */
  const isStageDeclined = (stageIndex) =>
    sessionStorage.getItem(`mmGiftStage${stageIndex}Declined`) === "true";
  const setStageDeclined = (stageIndex, declined) => {
    const key = `mmGiftStage${stageIndex}Declined`;
    if (declined) sessionStorage.setItem(key, "true");
    else sessionStorage.removeItem(key);
  };

  /* ================================================================
   * TAG-BASED QTY ENFORCEMENT
   *
   * Any product with tag "freegift" or "free-gift" must have qty <= 1.
   * Uses data-has-gift-tag attribute set by Liquid (server-side, cookie-independent).
   * Also enforces on all configured gift variant IDs.
   * ================================================================ */

  const enforceGiftQtyCaps = async () => {
    if (state.isQtyEnforcementRunning) return;
    state.isQtyEnforcementRunning = true;

    try {
      const cart = await fetchCart();
      const updates = {};

      // Collect all known gift variant IDs from:
      //  - DOM line-items carrying the freegift tag (server-side flag)
      //  - All configured stages
      const giftVariantIds = new Set();

      document.querySelectorAll('#cart-drawer line-item[data-has-gift-tag="true"]').forEach((el) => {
        if (el.dataset.variantId) giftVariantIds.add(parseInt(el.dataset.variantId));
      });
      getAllStageVariantIds().forEach((id) => giftVariantIds.add(id));

      // Cap qty to 1 for all gift items
      for (const item of cart.items) {
        if (giftVariantIds.has(item.variant_id) && item.quantity > 1) {
          updates[item.variant_id] = 1;
          if (DEBUG) console.warn(`[MM Cart] Capping gift qty: variant ${item.variant_id} from ${item.quantity} to 1`);
        }
      }

      if (Object.keys(updates).length > 0) {
        await cartUpdate(updates);
      }
    } catch (err) {
      console.error("[MM Cart] Qty enforcement error:", err);
    } finally {
      state.isQtyEnforcementRunning = false;
    }
  };

  /* ================================================================
   * STALE GIFT CHECK + AUTO-REMOVE (drawer-open / refresh only)
   *
   * Finds gifts that are either:
   * - Tagged as gift but no longer in config (orphaned)
   * - In config but no longer available (out of stock / unpublished)
   *
   * Such gifts are AUTOMATICALLY removed from the cart (like the old
   * live theme) so they can never reach checkout — a bare warning banner
   * let both the stale and the new gift through to checkout. Runs only on
   * drawer open / cart:refresh / 422 (NOT inside the observer loop), with
   * a re-entrancy guard; findStaleGifts re-fetches /cart.js fresh, so after
   * removal the next pass finds nothing → no loop.
   * ================================================================ */

  const STALE_GIFT_BANNER_ID = "mm-stale-gift-banner";

  const findStaleGifts = async () => {
    const cart = await fetchCart();
    const issues = [];

    const configuredIds = new Set(getAllStageVariantIds());

    for (const item of cart.items) {
      const lineItemEl = document.querySelector(
        `#cart-drawer line-item[data-variant-id="${item.variant_id}"]`
      );
      const hasGiftTag = lineItemEl?.dataset.hasGiftTag === "true";

      if (!hasGiftTag && !configuredIds.has(item.variant_id)) continue;

      if (hasGiftTag && !configuredIds.has(item.variant_id)) {
        issues.push({ kind: "orphaned", variantId: item.variant_id, item });
        continue;
      }

      if (configuredIds.has(item.variant_id)) {
        const available = await isVariantAvailable(item.variant_id);
        if (!available) {
          issues.push({ kind: "unavailable", variantId: item.variant_id, item });
        }
      }
    }

    return issues;
  };

  const removeStaleGiftBanner = () => {
    const el = document.getElementById(STALE_GIFT_BANNER_ID);
    if (el) el.remove();
  };

  // Auto-Remove statt Banner: nicht-mehr-konfigurierte ("orphaned") oder
  // nicht-verfügbare Geschenke werden automatisch entfernt, damit sie nicht in
  // den Checkout gelangen (Verhalten wie altes Live-Theme). Re-Entrancy-Guard
  // verhindert Überlappung; frisches /cart.js im Re-Check verhindert Loop.
  let isResolvingStaleGifts = false;

  const resolveStaleGifts = async () => {
    if (isResolvingStaleGifts) return;
    isResolvingStaleGifts = true;
    try {
      removeStaleGiftBanner(); // evtl. Rest eines alten Banners entfernen
      const issues = await findStaleGifts();
      if (issues.length === 0) return;

      const updates = {};
      issues.forEach((i) => { updates[i.variantId] = 0; });
      await cartUpdate(updates); // entfernt -> cart:refresh -> Re-Render
    } catch (err) {
      console.error("[MM Cart] Stale gift auto-remove error:", err);
    } finally {
      isResolvingStaleGifts = false;
    }
  };

  /* ================================================================
   * GIFT STAGES — unified pipeline
   *
   * One handler iterates all four configured stages in order. Each
   * stage is independent: the customer can hold up to four gifts at
   * once (one per enabled stage). Per-stage decline flags survive
   * the tab session and reset when the customer drops below the
   * stage's threshold.
   *
   * Auto-add is honoured only when Liquid pre-computed `autoAddEffective`
   * (admin set auto_add AND exactly one product in the stage). Multi-
   * product stages always require a user selection through the slider.
   * ================================================================ */

  const handleGiftStages = async (initialCart) => {
    if (state.isGiftProcessing) return;
    state.isGiftProcessing = true;

    try {
      const stages = getEnabledStages();
      if (stages.length === 0) return;

      let currentCart = initialCart;

      // No-real-items gate. Gifts are shippable products, so leaving
      // them in the cart after the customer removed every real item
      // would force a shipping address on a basket that's effectively
      // free. Strip every stage gift and bail out.
      //
      // Matches by variant_id OR product_id (isStageGiftItem) — a stage
      // gift in cart with a different variant than the one captured in
      // mmCartConfig (e.g. stale selected_or_first_available_variant)
      // is otherwise invisible to a variant-only filter and lingers
      // forever, which kept the drawer in its non-empty state when it
      // should have been empty.
      if (!hasShippableItems(currentCart)) {
        const updates = {};
        (currentCart.items || []).forEach((item) => {
          if (isStageGiftItem(item)) {
            updates[item.variant_id] = 0;
          }
        });
        if (Object.keys(updates).length > 0) {
          await cartUpdate(updates);
        }
        return;
      }

      // Re-fetch only after a stage actually wrote to the cart. The
      // initial cart from update() is good enough for the first stage,
      // and most stages will be no-ops on a given pass (either nothing
      // to do, or below threshold).
      for (const stage of stages) {
        const subtotal = calcSubtotalExcludingGifts(currentCart);
        const mutated = await processStage(stage, currentCart, subtotal);
        if (mutated) {
          currentCart = await fetchCart();
        }
      }
    } catch (err) {
      console.error("[MM Cart] handleGiftStages error:", err);
    } finally {
      state.isGiftProcessing = false;
    }
  };

  /**
   * Process a single stage. Returns `true` iff this call actually wrote
   * to the cart (so handleGiftStages knows whether to refresh the cart
   * for the next stage).
   */
  const processStage = async (stage, cart, subtotal) => {
    const stageIdx = stage.index;
    const threshold = parseFloat(stage.threshold || 0);
    const stageVariantIds = (stage.products || []).map((p) => parseInt(p.variantId));
    const itemsInCart = (cart.items || []).filter((i) => stageVariantIds.includes(i.variant_id));

    // Below threshold: clear decline flag, remove any item that's still
    // hanging around from a higher previous basket value.
    if (subtotal < threshold) {
      setStageDeclined(stageIdx, false);
      if (itemsInCart.length > 0) {
        const updates = {};
        itemsInCart.forEach((i) => { updates[i.variant_id] = 0; });
        await cartUpdate(updates);
        return true;
      }
      return false;
    }

    // Above threshold + already declined: nothing to do
    if (isStageDeclined(stageIdx)) return false;

    // Multiple of this stage in cart → keep first, remove rest, cap qty.
    if (itemsInCart.length > 1) {
      const updates = {};
      itemsInCart.forEach((item, i) => {
        if (i === 0) {
          if (item.quantity > 1) updates[item.variant_id] = 1;
        } else {
          updates[item.variant_id] = 0;
        }
      });
      if (Object.keys(updates).length > 0) {
        await cartUpdate(updates);
        return true;
      }
      return false;
    }

    // Exactly one item of this stage → ensure qty 1
    if (itemsInCart.length === 1) {
      if (itemsInCart[0].quantity > 1) {
        await cartUpdate({ [itemsInCart[0].variant_id]: 1 });
        return true;
      }
      return false;
    }

    // No item of this stage in the cart. Auto-add if allowed.
    if (stage.autoAddEffective && stage.products && stage.products.length === 1) {
      const variantId = parseInt(stage.products[0].variantId);
      if (await isVariantAvailable(variantId)) {
        await cartUpdate({ [variantId]: 1 });
        return true;
      }
    }
    // Manual stage, wait for the customer to pick in the slider.
    return false;
  };

  /** Customer clicks "Auswählen" on a slider card. */
  const selectStageGift = async (variantId) => {
    const found = findStageByVariantId(variantId);
    if (!found) return;
    const { stage, index: stageIdx } = found;

    if (state.isGiftProcessing) {
      await new Promise((r) => setTimeout(r, 150));
    }
    state.isGiftProcessing = true;

    try {
      setStageDeclined(stageIdx, false);

      const cart = await fetchCart();

      // Gate: a click on the slider should never push a gift onto a
      // digital-only basket. Could happen if a CSS override or a
      // keyboard-driven user activates a locked-but-still-in-DOM
      // button.
      if (!hasShippableItems(cart)) {
        window.unlockCheckoutButton();
        return;
      }

      const stageVariantIds = (stage.products || []).map((p) => parseInt(p.variantId));
      const updates = {};

      // Remove any other product of the same stage already in cart
      (cart.items || []).forEach((item) => {
        if (stageVariantIds.includes(item.variant_id) && item.variant_id !== variantId) {
          updates[item.variant_id] = 0;
        }
      });

      // Availability check before we propose to add the chosen variant
      const initiallyInCart = (cart.items || []).some((i) => i.variant_id === variantId);
      if (!initiallyInCart) {
        if (!(await isVariantAvailable(variantId))) {
          window.unlockCheckoutButton();
          if (Object.keys(updates).length > 0) await cartUpdate(updates);
          return;
        }
        updates[variantId] = 1;
      }

      // Race-condition guard (mirrors the old selectGift2): re-fetch
      // right before the write to confirm the variant hasn't shown up
      // via another path (parallel tab, app webhook, etc.). If it has,
      // skip the add part — cart/update.js SET semantics would clamp
      // qty back to 1 anyway, but skipping the round-trip is cheaper
      // and avoids surfacing a no-op cart:refresh to the rest of the
      // pipeline.
      if (Object.keys(updates).length > 0) {
        const freshCart = await fetchCart();
        const freshlyInCart = (freshCart.items || []).some((i) => i.variant_id === variantId);
        if (freshlyInCart && !initiallyInCart) {
          delete updates[variantId];
          if (DEBUG) console.log("[MM Cart] selectStageGift: race-detected, skipping add");
        }
        if (Object.keys(updates).length > 0) {
          await cartUpdate(updates);
        }
      }
    } catch (err) {
      console.error("[MM Cart] selectStageGift error:", err);
    } finally {
      state.isGiftProcessing = false;
    }
  };

  /** Customer clicks "Entfernen" — declines the entire stage. */
  const declineStage = async (stageIdx) => {
    if (state.isGiftProcessing) {
      await new Promise((r) => setTimeout(r, 150));
    }
    state.isGiftProcessing = true;

    try {
      setStageDeclined(stageIdx, true);

      const stage = getStages().find((s) => s && s.index === stageIdx);
      if (!stage) return;
      const stageVariantIds = (stage.products || []).map((p) => parseInt(p.variantId));

      const cart = await fetchCart();
      const updates = {};
      (cart.items || []).forEach((item) => {
        if (stageVariantIds.includes(item.variant_id)) {
          updates[item.variant_id] = 0;
        }
      });

      if (Object.keys(updates).length > 0) await cartUpdate(updates);
    } catch (err) {
      console.error("[MM Cart] declineStage error:", err);
    } finally {
      state.isGiftProcessing = false;
    }
  };

  /**
   * Customer clicks "Anderes Geschenk" in a line item — remove the
   * current gift of that stage without setting the declined flag, so
   * the slider re-offers the alternatives.
   */
  const changeStageGift = async (stageIdx) => {
    if (state.isGiftProcessing) {
      await new Promise((r) => setTimeout(r, 150));
    }
    state.isGiftProcessing = true;

    try {
      setStageDeclined(stageIdx, false);
      const stage = getStages().find((s) => s && s.index === stageIdx);
      if (!stage) return;
      const stageVariantIds = (stage.products || []).map((p) => parseInt(p.variantId));

      const cart = await fetchCart();
      const updates = {};
      (cart.items || []).forEach((item) => {
        if (stageVariantIds.includes(item.variant_id)) updates[item.variant_id] = 0;
      });
      if (Object.keys(updates).length > 0) await cartUpdate(updates);
    } catch (err) {
      console.error("[MM Cart] changeStageGift error:", err);
    } finally {
      state.isGiftProcessing = false;
    }
  };

  /**
   * Sync the data-state attribute on every slider card to one of:
   *   locked / available / active / declined
   * Called from update() on each cart pass.
   */
  const updateSliderStates = (cart) => {
    const cards = document.querySelectorAll("[data-mm-gift-card]");
    if (cards.length === 0) return;

    const subtotal = calcSubtotalExcludingGifts(cart);
    const inCartVariantIds = new Set((cart.items || []).map((i) => i.variant_id));
    // Digital-only basket: every card stays locked. Mirrors the gate
    // in handleGiftStages so a high digital subtotal can't visually
    // unlock cards the customer wouldn't be allowed to use.
    const shippable = hasShippableItems(cart);

    cards.forEach((card) => {
      const stageIdx = parseInt(card.dataset.stage);
      const stageThreshold = parseFloat(card.dataset.stageThreshold || 0);
      const variantId = parseInt(card.dataset.variantId);
      const declined = isStageDeclined(stageIdx);

      let nextState = "locked";
      if (shippable && subtotal >= stageThreshold) {
        if (inCartVariantIds.has(variantId)) {
          nextState = "active";
        } else if (declined) {
          nextState = "declined";
        } else {
          nextState = "available";
        }
      }
      card.setAttribute("data-state", nextState);
    });
  };

  /**
   * Find the card whose horizontal center is closest to the carousel's
   * viewport center, then write its threshold into the headline span
   * and flip aria-current on the matching page-dot.
   */
  const updateHeadlineFromCenterCard = () => {
    const slider = document.querySelector("[data-mm-gift-slider]");
    if (!slider) return;
    const carousel = slider.querySelector("scroll-carousel");
    if (!carousel) return;

    const cards = carousel.querySelectorAll("[data-mm-gift-card]");
    if (cards.length === 0) return;

    const carouselRect = carousel.getBoundingClientRect();
    const carouselCenterX = carouselRect.left + carouselRect.width / 2;
    let closestCard = cards[0];
    let closestDistance = Infinity;

    cards.forEach((card) => {
      const rect = card.getBoundingClientRect();
      const cardCenterX = rect.left + rect.width / 2;
      const distance = Math.abs(cardCenterX - carouselCenterX);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestCard = card;
      }
    });

    const threshold = closestCard.dataset.stageThreshold;
    const headlineEl = document.querySelector("[data-mm-gift-headline-threshold]");
    if (headlineEl && threshold) {
      headlineEl.textContent = `${threshold} €`;
    }

    const variantId = closestCard.dataset.variantId;
    document.querySelectorAll("[data-mm-gift-dot]").forEach((dot) => {
      dot.setAttribute("aria-current", dot.dataset.variantId === variantId ? "true" : "false");
    });
  };

  /* ================================================================
   * PROGRESS BAR
   *
   * The visible progress / cheering bar comes from
   * `snippets/cart-progress-bar.liquid`. It computes its own values
   * server-side from cart.items (incl. cart-level discount) and is
   * re-rendered fresh on every cart:refresh, just like the other
   * Liquid-rendered Drawer values. No JS-side state to keep in sync —
   * the snippet hides itself completely on digital-only baskets.
   * ================================================================ */

  const showHideCheeringBar = (cart) => {
    // Placeholder kept for backwards-compat with callers; the actual
    // visibility is now handled by cart-progress-bar.liquid via the
    // has_shippable Liquid guard.
    void cart;
  };

  /* ================================================================
   * SHIPPING LINE
   *
   * Liquid already renders Subtotal, Total and Checkout-Button price
   * fresh on every cart:change (CartDrawer.replaceContent_fn replaces
   * the section children with server-rendered HTML). We DO NOT
   * overwrite those values — doing so on stale DOM-attribute reads was
   * the previous "summe wird nicht aktualisiert" bug.
   *
   * What JS still has to do is the country-aware shipping cost line.
   * Liquid only knows the DE default; for AT/CH (or non-supported
   * markets) we patch the rendered value here using detected country
   * from /browsing_context_suggestions.json.
   * ================================================================ */

  const updateShippingLine = (cart) => {
    /* TL-0091: querySelectorAll so Control + V1 + V2 shipping lines stay in sync. */
    const deliveryEls = document.querySelectorAll("[data-mm-delivery-cost]");
    if (!deliveryEls.length) return;

    const isSupportedCountry = ["DE", "AT", "CH"].includes(state.country);
    const netPrice = calcSubtotalExcludingGifts(cart);
    const isFreeShipping = !hasShippableItems(cart) || netPrice >= state.minFreeShipping;

    deliveryEls.forEach(function (deliveryEl) {
      if (!isSupportedCountry) {
        deliveryEl.textContent = "Wird im Checkout berechnet";
        deliveryEl.classList.remove("highlight-free-shipping");
        return;
      }

      if (isFreeShipping) {
        const label = deliveryEl.dataset.freeshippingText || "kostenlos";
        deliveryEl.innerHTML = '<span class="mm-price-summary__free">' + label + '</span>';
        deliveryEl.classList.add("highlight-free-shipping");
      } else {
        deliveryEl.textContent = formatMoney(state.shippingRate * 100);
        deliveryEl.classList.remove("highlight-free-shipping");
      }
    });

    /* TL-0091: keep V1/V2 Gesamt in sync with the shipping line. */
    document.querySelectorAll("[data-TL0091-total]").forEach(function (totalEl) {
      var subtotal = parseInt(totalEl.getAttribute("data-tl0091-subtotal"), 10) || 0;
      var shippingCents = 0;
      if (isSupportedCountry && !isFreeShipping) {
        shippingCents = state.shippingRate * 100;
      }
      var amount = ((subtotal + shippingCents) / 100).toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      totalEl.textContent = amount + "€";
    });
  };

  /* ================================================================
   * HANDLE OTHER LOCATIONS
   * ================================================================ */

  const handleOtherLocations = () => {
    const cheeringBar = document.querySelector("[data-mm-cheering-bar]");
    const totalRow = document.querySelector(".mm-price-summary__row--total");
    if (cheeringBar) cheeringBar.style.display = "none";
    if (totalRow) totalRow.style.display = "none";
  };

  /* ================================================================
   * CART UPDATE
   *
   * We use cart/update.js (SET semantics) rather than cart/add.js
   * (incrementing) for every gift mutation — that's what stopped the
   * "gift added 12 times" pattern from the old theme code: even if
   * two triggers fire in parallel, both set qty to 1 instead of each
   * adding 1.
   *
   * 422 handling: Shopify returns 422 when a variant is sold out or
   * unpublished. We mark the affected variants as unavailable in the
   * cache so the next drawer-open shows the sold-out banner instead
   * of silently retrying.
   * ================================================================ */

  const cartUpdate = async (updates) => {
    window.lockCheckoutButton();

    try {
      const resp = await fetch(window.Shopify.routes.root + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Cache-Control": "no-cache" },
        body: JSON.stringify({ updates }),
      });

      if (!resp.ok) {
        if (resp.status === 422) {
          // Inventory/availability problem — flag every variant we tried
          // to *add* (qty > 0) as unavailable so subsequent passes skip
          // it. Removals (qty 0) can't 422, so no cache work for those.
          Object.entries(updates).forEach(([variantId, qty]) => {
            if (qty > 0) {
              variantAvailabilityCache.set(parseInt(variantId), { v: false, t: Date.now() });
            }
          });

          // Surface the situation to the customer if drawer is visible.
          const drawer = getCartDrawer();
          if (drawer && drawer.getAttribute("aria-hidden") !== "true") {
            resolveStaleGifts();
          }
        }
        throw new Error("Cart update failed: " + resp.status);
      }

      // Trigger re-render (new Prestige theme)
      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));

      setTimeout(() => window.unlockCheckoutButton(), 1000);
    } catch (err) {
      console.error("[MM Cart] Cart update error:", err);
      setTimeout(() => window.unlockCheckoutButton(), 1000);
    }
  };

  /* ================================================================
   * COUPON TOGGLE (legacy, kept for older markup with
   * data-mm-coupon-toggle / data-mm-coupon-content. The current
   * cart-drawer.liquid renders tl-cart-discount.liquid which is
   * always visible — no toggle needed.)
   * ================================================================ */

  const initCouponToggle = () => {
    document.addEventListener("click", (e) => {
      const toggle = e.target.closest("[data-mm-coupon-toggle]");
      if (!toggle) return;
      const content = document.querySelector("[data-mm-coupon-content]");
      if (content) {
        content.style.display = content.style.display !== "none" ? "none" : "block";
      }
    });
  };

  /* ================================================================
   * TL CART DISCOUNT — visible Apply button + Enter handling
   *
   * Markup lives in snippets/tl-cart-discount.liquid. The actual
   * apply/remove logic stays with Prestige's <cart-discount-field>
   * web component (theme.js), which listens for `change` events on
   * itself and POSTs to /cart/update.js with the discount param.
   *
   * Two delegated listeners on document so the wiring survives
   * Prestige's section re-renders:
   *   (1) Click on [data-tl-cart-discount-apply] → dispatch `change`
   *       on the input → bubbles to <cart-discount-field> → apply.
   *   (2) Enter keydown inside [data-tl-cart-discount-input] →
   *       preventDefault() (otherwise the surrounding cart-footer form
   *       submits and routes the customer straight to checkout) →
   *       same `change` dispatch.
   * ================================================================ */

  const triggerTlCartDiscountApply = (input) => {
    if (!input) return;
    // Guard against empty submits. An empty value would still hit
    // cart/update.js with the existing discount string, which echoes
    // back via cart:refresh and re-runs the gift handlers — and that
    // path was occasionally yanking the auto-add stage gift out. Empty
    // = no-op (keep focus so the customer notices nothing happened).
    if (!input.value || !input.value.trim()) {
      input.focus();
      return;
    }
    // <cart-discount-field>.toggleDiscount reads event.target.value.
    // Bubbles up to the parent custom element via the default change
    // event behaviour.
    input.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const initTlCartDiscount = () => {
    if (window.__mmTlCartDiscountBound) return;
    window.__mmTlCartDiscountBound = true;

    document.addEventListener("click", (e) => {
      const btn = e.target.closest && e.target.closest("[data-tl-cart-discount-apply]");
      if (!btn) return;
      const wrap = btn.closest("cart-discount-field");
      const input = wrap && wrap.querySelector("[data-tl-cart-discount-input]");
      triggerTlCartDiscountApply(input);
    });

    document.addEventListener("keydown", (e) => {
      if (e.key !== "Enter") return;
      const input = e.target && e.target.closest && e.target.closest("[data-tl-cart-discount-input]");
      if (!input) return;
      e.preventDefault();   // STOP the cart-footer form from submitting → no jump to checkout
      triggerTlCartDiscountApply(input);
    });
  };

  /* ================================================================
   * FOOTER INFO TOOLTIP
   *
   * The footer info banner (.mm-footer-info) has an optional "i" button
   * top-right that toggles a tooltip with detail content. Mirrors the
   * live theme's sh-cart-banner popup, but as a small tooltip instead
   * of a fullscreen overlay.
   *
   * Delegated on document so it survives Prestige's section re-renders.
   * ================================================================ */

  const closeFooterInfoTooltip = (banner) => {
    if (!banner) return;
    const tip = banner.querySelector("[data-mm-footer-info-tooltip]");
    const btn = banner.querySelector("[data-mm-footer-info-toggle]");
    if (tip) tip.hidden = true;
    if (btn) btn.setAttribute("aria-expanded", "false");
  };

  const anyFooterInfoTooltipOpen = () => {
    let openBanner = null;
    document.querySelectorAll("[data-mm-footer-info]").forEach((b) => {
      const tip = b.querySelector("[data-mm-footer-info-tooltip]");
      if (tip && !tip.hidden) openBanner = b;
    });
    return openBanner;
  };

  const initFooterInfoTooltip = () => {
    if (window.__mmFooterInfoTooltipBound) return;
    window.__mmFooterInfoTooltipBound = true;

    // Modal-style outside-press behaviour: when an outside click would
    // close the tooltip, we also SWALLOW the underlying click so that
    // e.g. Add-to-Cart / Checkout don't fire on the same gesture. The
    // tooltip closes on pointerdown (to beat the cart-drawer's overlay
    // click-handler), and the subsequent `click` event of the same
    // gesture is swallowed via this timestamp.
    let swallowNextClickUntil = 0;

    const handleOutsidePress = (e) => {
      // Inside an open tooltip → don't interfere (allow text selection,
      // clicks on links etc.).
      const insideOpenTip = e.target.closest && e.target.closest("[data-mm-footer-info-tooltip]");
      if (insideOpenTip && !insideOpenTip.hidden) return false;

      // Toggle / close button is handled by the click listener below.
      if (e.target.closest && (
        e.target.closest("[data-mm-footer-info-toggle]") ||
        e.target.closest("[data-mm-footer-info-close]")
      )) return false;

      // Trailing click of a gesture whose pointerdown already closed
      // the tooltip → swallow so the underlying button doesn't fire.
      if (e.type === "click" && Date.now() < swallowNextClickUntil) {
        swallowNextClickUntil = 0;
        e.stopImmediatePropagation();
        e.stopPropagation();
        e.preventDefault();
        return true;
      }

      const openBanner = anyFooterInfoTooltipOpen();
      if (!openBanner) return false;

      // Outside-press while a tooltip is open → close it. Swallow the
      // entire gesture so the cart-drawer's overlay-click handler (in
      // shadow DOM) doesn't close the whole drawer, and so the
      // underlying control (Add-to-Cart, Checkout, etc.) doesn't fire.
      closeFooterInfoTooltip(openBanner);
      if (e.type === "pointerdown" || e.type === "mousedown") {
        // Mark the subsequent click of this gesture for suppression.
        swallowNextClickUntil = Date.now() + 600;
      }
      e.stopImmediatePropagation();
      e.stopPropagation();
      e.preventDefault();
      return true;
    };

    // Capture phase on document AND on <cart-drawer> if present — the
    // drawer hosts a shadow root, and the overlay-click hide() listener
    // sits inside that shadow. Listening on the drawer element itself
    // (capture) means we see the event as it descends into the shadow.
    const bindOutside = (target) => {
      target.addEventListener("pointerdown", handleOutsidePress, true);
      target.addEventListener("mousedown", handleOutsidePress, true);
      target.addEventListener("click", handleOutsidePress, true);
    };

    bindOutside(document);
    const drawerEl = document.querySelector("cart-drawer");
    if (drawerEl) bindOutside(drawerEl);

    // Toggle button + close button: handled on `click` so that single
    // taps fire reliably. Stops propagation so the same click does not
    // also trigger the outside-press handler above (which would have
    // already returned false for these, but belt-and-suspenders).
    document.addEventListener("click", (e) => {
      const toggle = e.target.closest && e.target.closest("[data-mm-footer-info-toggle]");
      if (toggle) {
        const banner = toggle.closest("[data-mm-footer-info]");
        const tip = banner && banner.querySelector("[data-mm-footer-info-tooltip]");
        if (!tip) return;
        const wasOpen = !tip.hidden;
        document.querySelectorAll("[data-mm-footer-info]").forEach((b) => {
          if (b !== banner) closeFooterInfoTooltip(b);
        });
        tip.hidden = wasOpen;
        toggle.setAttribute("aria-expanded", String(!wasOpen));
        e.stopPropagation();
        return;
      }

      const close = e.target.closest && e.target.closest("[data-mm-footer-info-close]");
      if (close) {
        closeFooterInfoTooltip(close.closest("[data-mm-footer-info]"));
        e.stopPropagation();
        return;
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      const openBanner = anyFooterInfoTooltipOpen();
      if (!openBanner) return;
      closeFooterInfoTooltip(openBanner);
      e.stopPropagation();
      e.preventDefault();
    }, true);
  };

  /* ================================================================
   * MAIN UPDATE (debounced, idempotent)
   *
   * The update() flow has two responsibilities:
   *  - Cheap DOM refresh (price summary, cheering bar visibility)
   *  - Cart mutations (qty caps, gift add/remove)
   *
   * Cart mutations must NOT run on every MutationObserver tick — they
   * re-write DOM, which triggers the observer again. We protect against
   * that with two layers:
   *   1) `state.isOurUpdate` flag — observer skips ticks while we're
   *      mid-update (synchronous-ish guard).
   *   2) Cart-state hash — if cart contents are unchanged since the last
   *      mutation pass, we skip the expensive Gift-/Cleanup path
   *      entirely. DOM-only refresh still runs (cheap).
   * ================================================================ */

  const computeCartHash = (cart) => {
    if (!cart) return "";
    const items = (cart.items || [])
      .map((i) => `${i.variant_id}:${i.quantity}`)
      .sort()
      .join("|");
    return `${cart.token || ""}#${cart.item_count || 0}#${cart.total_price || 0}#${items}#${state.country || ""}`;
  };

  let updateDebounceTimer = null;
  let updateNeedsRerun = false;

  const update = () => {
    clearTimeout(updateDebounceTimer);
    updateDebounceTimer = setTimeout(async () => {
      if (state.isOurUpdate) {
        // Re-entry: don't drop the tick — remember to run again after
        // the current pass finishes.
        updateNeedsRerun = true;
        return;
      }

      state.isOurUpdate = true;
      try {
        // Fetch cart once, hand it to every consumer that needs derived
        // values. Subtotal, Total and Checkout-Button price come from
        // Liquid (rendered fresh in the section HTML on cart:change);
        // we only touch the shipping line, cheering bar and gift slots.
        let cart;
        try {
          cart = await fetchCart();
        } catch (err) {
          console.warn("[MM Cart] update() fetchCart failed:", err);
          return;
        }

        const isSupportedCountry = ["DE", "AT", "CH"].includes(state.country);

        // Country-dependent shipping line (Liquid only knows the DE default).
        // The progress bar handles its own visibility in Liquid.
        updateShippingLine(cart);

        if (!isSupportedCountry) {
          handleOtherLocations();
          window.unlockCheckoutButton();
          return;
        }

        // Hash gate: skip cart mutations when nothing actually changed.
        const hash = computeCartHash(cart);
        if (hash === state.lastCartHash) {
          if (DEBUG) console.log("[MM Cart] update() skipped — cart hash unchanged");
          window.unlockCheckoutButton();
          return;
        }
        state.lastCartHash = hash;
        state.lastCartToken = cart.token || null;

        // Slider-state sync — purely visual, no cart writes.
        updateSliderStates(cart);
        updateHeadlineFromCenterCard();

        // Enforce qty caps first (tag-based + config-based)
        await enforceGiftQtyCaps();

        // Stage processing — auto-add, removal, qty caps per stage.
        await handleGiftStages(cart);

        // Nicht-mehr-konfigurierte / unavailable Geschenke direkt hier im
        // update()-Pass entfernen (NICHT erst beim Drawer-Öffnen) — sonst
        // blitzt beim Öffnen kurz das alte Geschenk auf, bevor es weg ist.
        // update() läuft schon beim Load (detectCountry -> update) und ist
        // hash-gegatet (nur bei echter Cart-Änderung), sequenziell nach
        // handleGiftStages -> keine Race. Der Drawer-Open-Hook bleibt Fallback.
        await resolveStaleGifts();
      } finally {
        // Release re-entry guard on next tick so any synchronous-trailing
        // DOM mutations are absorbed by the same flag window.
        setTimeout(() => {
          state.isOurUpdate = false;
          if (updateNeedsRerun) {
            updateNeedsRerun = false;
            update();
          }
        }, 0);
      }
    }, 80);
  };

  /* ================================================================
   * DECLINE UNDO
   *
   * A declined stage was previously a one-way door: setStageDeclined()
   * is only ever cleared again in processStage() when the basket drops
   * BELOW that stage's threshold. A customer who declined at 85 EUR and
   * stayed above it could not get the gift back at all — not by adding
   * more, not by reloading. The Cheering Bar marker exposes an undo and
   * calls in here.
   * ================================================================ */

  /** Is this stage currently declined? (read by the Cheering Bar) */
  window.mmIsGiftStageDeclined = (stageIdx) => isStageDeclined(parseInt(stageIdx));

  /** Clears the decline flag and lets the normal pass re-offer the gift. */
  window.mmReactivateGiftStage = (stageIdx) => {
    const idx = parseInt(stageIdx);
    if (!idx) return;
    setStageDeclined(idx, false);
    document.dispatchEvent(
      new CustomEvent("mm:gift-stage-reactivated", { detail: { stage: idx } })
    );
    // update() is hash-gated: it skips the whole stage pass when the cart
    // itself is unchanged. Reactivating changes only the sessionStorage flag,
    // so without clearing the hash the pass would return before
    // handleGiftStages() and the gift would appear only on some later,
    // unrelated cart change.
    state.lastCartHash = null;
    update();
  };

  /* ================================================================
   * GIFT EVENT LISTENERS (Event Delegation)
   * One document-level click listener that routes to stage handlers
   * based on the data-* attribute on the closest matching element.
   * ================================================================ */

  const initGiftEventListeners = () => {
    document.addEventListener("click", (e) => {
      // Slider card: "Auswählen"
      const selectBtn = e.target.closest("[data-mm-gift-select]");
      if (selectBtn) {
        const card = selectBtn.closest("[data-mm-gift-card]");
        const variantId = card?.dataset.variantId;
        if (variantId) selectStageGift(parseInt(variantId));
        return;
      }

      // Slider card: "Entfernen"  (declines the whole stage)
      const declineBtn = e.target.closest("[data-mm-gift-decline]");
      if (declineBtn) {
        const card = declineBtn.closest("[data-mm-gift-card]");
        const stageIdx = parseInt(card?.dataset.stage);
        if (stageIdx) declineStage(stageIdx);
        return;
      }

      // Line item: "Entfernen" (line-item-level decline of the stage)
      const lineItemDeclineBtn = e.target.closest("[data-mm-line-item-gift-decline]");
      if (lineItemDeclineBtn) {
        const stageIdx = parseInt(lineItemDeclineBtn.dataset.stage);
        if (stageIdx) declineStage(stageIdx);
        return;
      }

      // Line item: "Anderes Geschenk" (remove current, keep stage open)
      const lineItemChangeBtn = e.target.closest("[data-mm-line-item-gift-change]");
      if (lineItemChangeBtn) {
        const stageIdx = parseInt(lineItemChangeBtn.dataset.stage);
        if (stageIdx) changeStageGift(stageIdx);
        return;
      }

      // Slider dot: scroll the carousel to the corresponding card.
      const dotBtn = e.target.closest("[data-mm-gift-dot]");
      if (dotBtn) {
        const variantId = dotBtn.dataset.variantId;
        const card = document.querySelector(
          `[data-mm-gift-card][data-variant-id="${variantId}"]`
        );
        card?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
        return;
      }

      // Tag-only fallback: remove a gift without a configured stage.
      const removeGenericBtn = e.target.closest("[data-mm-remove-gift-generic]");
      if (removeGenericBtn) {
        const variantId = removeGenericBtn.dataset.variantId;
        if (variantId) cartUpdate({ [parseInt(variantId)]: 0 });
        return;
      }
    });
  };

  /** Wire the slider's scroll-end events to refresh the headline. */
  const initSliderHeadlineSync = () => {
    const slider = document.querySelector("[data-mm-gift-slider]");
    if (!slider) return;
    const carousel = slider.querySelector("scroll-carousel");
    if (!carousel) return;

    let scrollTimer;
    carousel.addEventListener("scroll", () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(updateHeadlineFromCenterCard, 80);
    });
    // Initial paint
    requestAnimationFrame(updateHeadlineFromCenterCard);
  };

  /* ================================================================
   * INIT
   * ================================================================ */

  /* ================================================================
   * STALE GIFT DRAWER HOOKS
   *
   * Hook into the <cart-drawer> web component's own lifecycle so the
   * sold-out check runs exactly when the customer can see the result
   * — and not on every silent cart update.
   * ================================================================ */

  const initStaleGiftDrawerHooks = () => {
    const drawer = getCartDrawer();
    if (!drawer) return;

    // Drawer opens: run the check before the first paint of items, and
    // refresh the slider headline because the carousel only has real
    // dimensions once the drawer is on screen.
    drawer.addEventListener("dialog:before-show", () => {
      resolveStaleGifts();
      requestAnimationFrame(updateHeadlineFromCenterCard);
    });

    // After a server-side re-render of the drawer, re-check IF the
    // drawer is currently visible (banner + slider DOM are replaced by
    // the refresh). Use aria-hidden as the visibility proxy.
    drawer.addEventListener("cart-drawer:refreshed", () => {
      if (drawer.getAttribute("aria-hidden") !== "true") {
        resolveStaleGifts();
        // Re-attach scroll listener to the freshly-rendered carousel.
        initSliderHeadlineSync();
        requestAnimationFrame(updateHeadlineFromCenterCard);
      }
    });
  };

  /* ================================================================
   * PRIORITY-PROCESSING TOGGLE
   *
   * Adds/removes the configured service product. The line item is
   * stamped with properties._hide_from_cart so Liquid can skip it in
   * the drawer item list and on /cart — checkout still shows it.
   * ================================================================ */

  const HIDE_FROM_CART_PROP = "_hide_from_cart";

  const isPriorityLineItem = (item) => {
    const priority = cfg().priorityProcessing || {};
    if (!priority.enabled || !priority.productId) return false;
    if (item.product_id === priority.productId) return true;
    const props = item.properties || {};
    return props[HIDE_FROM_CART_PROP] === "true" || props[HIDE_FROM_CART_PROP] === true;
  };

  const findPriorityLine = (cart) => (cart.items || []).find(isPriorityLineItem) || null;

  const countVisibleItems = (cart) =>
    (cart.items || []).filter((item) => !isPriorityLineItem(item)).reduce((sum, item) => sum + item.quantity, 0);

  const priorityCartRequest = async (endpoint, payload) => {
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    const body = Object.assign({}, payload, {
      sections: sectionsToBundle.join(","),
    });

    const resp = await fetch(window.Shopify.routes.root + endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });

    if (!resp.ok) {
      const errBody = await resp.json().catch(() => ({}));
      throw new Error(errBody.description || "Priority cart update failed: " + resp.status);
    }

    return resp.json();
  };

  const addPriorityProduct = async (variantId) => {
    // Avoid duplicate lines if the toggle races a previous add.
    const existing = findPriorityLine(await fetchCart());
    if (existing) {
      if (existing.quantity > 1) {
        const updates = {};
        updates[String(existing.variant_id)] = 1;
        return cartUpdate(updates).then(() => fetchCart());
      }
      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      return fetchCart();
    }

    const addResponse = await priorityCartRequest("cart/add.js", {
      items: [
        {
          id: Number(variantId),
          quantity: 1,
          properties: { [HIDE_FROM_CART_PROP]: "true" },
        },
      ],
    });

    // Match Prestige ProductForm: full cart.js + sections from add response.
    const cart = await fetchCart();
    cart.sections = addResponse.sections || {};

    if (cart.sections && Object.keys(cart.sections).length) {
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "priority-toggle:add",
            cart,
          },
        })
      );
    } else {
      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    }

    return cart;
  };

  const removePriorityProduct = async (variantId) => {
    const updates = {};
    updates[String(variantId)] = 0;

    const cartContent = await priorityCartRequest("cart/update.js", { updates });

    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "priority-toggle:remove",
          cart: cartContent,
        },
      })
    );

    return cartContent;
  };

  let priorityCleanupRunning = false;

  const cleanupOrphanPriority = async () => {
    const priority = cfg().priorityProcessing || {};
    if (!priority.enabled || !priority.variantId) return;
    if (priorityCleanupRunning) return;
    priorityCleanupRunning = true;

    try {
      const cart = await fetchCart();
      const priorityLine = findPriorityLine(cart);
      if (!priorityLine) return;

      // Drop the service when no visible products remain.
      if (countVisibleItems(cart) === 0) {
        await removePriorityProduct(priorityLine.variant_id);
        return;
      }

      // Enforce qty 1 for the service product.
      if (priorityLine.quantity > 1) {
        const updates = {};
        updates[String(priorityLine.variant_id)] = 1;
        await cartUpdate(updates);
      }
    } catch (err) {
      console.warn("[MM Cart] Priority cleanup failed:", err);
    } finally {
      priorityCleanupRunning = false;
    }
  };

  const initPriorityToggle = () => {
    document.addEventListener("change", async (e) => {
      const input = e.target.closest?.("[data-tl-priority-input]");
      if (!input) return;

      const root = input.closest("[data-tl-priority-toggle]");
      if (!root) return;

      const variantId = root.dataset.variantId;
      if (!variantId) return;

      const wantOn = input.checked;
      root.classList.add("is-busy");
      input.disabled = true;
      if (typeof window.lockCheckoutButton === "function") window.lockCheckoutButton();

      try {
        if (wantOn) {
          await addPriorityProduct(variantId);
          // Goal: toggle ON
          document.documentElement.dispatchEvent(
            new CustomEvent("tl:priority-toggle", {
              bubbles: true,
              detail: { state: "on", variantId: Number(variantId) },
            })
          );
        } else {
          await removePriorityProduct(variantId);
          // Goal: toggle OFF
          document.documentElement.dispatchEvent(
            new CustomEvent("tl:priority-toggle", {
              bubbles: true,
              detail: { state: "off", variantId: Number(variantId) },
            })
          );
        }
      } catch (err) {
        console.error("[MM Cart] Priority toggle failed:", err);
        input.checked = !wantOn;
      } finally {
        root.classList.remove("is-busy");
        input.disabled = false;
        setTimeout(() => {
          if (typeof window.unlockCheckoutButton === "function") window.unlockCheckoutButton();
        }, 400);
      }
    });

    ["cart:change", "cart:refresh"].forEach((evt) => {
      document.addEventListener(evt, () => {
        setTimeout(cleanupOrphanPriority, 250);
      });
    });
  };

  document.addEventListener("DOMContentLoaded", () => {
    window.unlockCheckoutButton();
    detectCountry();
    initCouponToggle();
    initTlCartDiscount();
    initFooterInfoTooltip();
    initGiftEventListeners();
    initSliderHeadlineSync();
    initStaleGiftDrawerHooks();
    initPriorityToggle();

    // Cart update events (new Prestige theme)
    ["cart:refresh", "cart:change"].forEach((evt) => {
      document.addEventListener(evt, () => setTimeout(update, 100));
    });

    // dcart sc:discount.* listeners removed (commit f46b4e1 → reverted):
    // calling update() on those events caused gift stages to flip to
    // "inactive" on discount apply and stay stuck until full page reload
    // (cart-hash guard skipped the recovery pass on discount remove).
    // dcart applies discounts through Shopify's cart/update.js, which
    // Prestige's own cart system already turns into a cart:refresh —
    // so the listeners above pick that up. If price summary / cheering
    // bar do NOT update on discount apply, we'll need a more targeted
    // hook (not a blanket update() trigger).

    // MutationObserver fallback — only reacts to *structural* changes
    // (line items added/removed, footer re-rendered). Style/textContent
    // tweaks inside the drawer don't trigger an update, which prevents
    // the previous self-feeding loop where update() re-wrote text →
    // observer fired → update() ran again.
    const cartDrawer = getCartDrawer();
    if (cartDrawer) {
      const observer = new MutationObserver((mutations) => {
        if (state.isOurUpdate) return;

        const significant = mutations.some((m) => {
          if (m.type !== "childList") return false;
          if (m.addedNodes.length === 0 && m.removedNodes.length === 0) return false;
          const t = m.target;
          return (
            t.classList?.contains("cart-drawer__items") ||
            t.hasAttribute?.("slot") ||
            t.closest?.(".cart-drawer__items") ||
            t.closest?.('[slot="footer"]')
          );
        });
        if (!significant) return;

        clearTimeout(observer._timeout);
        observer._timeout = setTimeout(update, 150);
      });
      observer.observe(cartDrawer, { childList: true, subtree: true });
    }

    // Legacy event
    document.addEventListener("rerenderCart", () => setTimeout(update, 100));
  });
})();